import { ChevronLeft, Download, FileArchive, UserPlusIcon } from 'lucide-react'
import { useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { partnersProspects } from 'rails_routes'
import { Button, ButtonLink, FileList, IconLink, Tabs, TextLink } from 'slab'
import { getFormattedDate } from 'utils'
import { getGetQuotingProspectsProspectIdUploadsCensusUrl, getGetQuotingProspectsProspectIdUploadsOtherFilesBlobIdUrl, getGetQuotingProspectsProspectIdUploadsSbcsBlobIdUrl, useGetQuotingProspectsId } from 'utils/web_api'
import { type QuotingProspect } from 'utils/web_api.schemas'

import { CreateQuoteButton } from './CreateQuoteButton'
import { CreateQuoteFailed, GettingProspectFailed } from './GetErrorBanners'
import { ProspectEmployeeCensusList } from './ProspectEmployeeCensusList'
import { QuotesTable } from './QuotesList'
import { SkeletonItem } from '../reusable/SkeletonItem'

interface ProspectInfoSectionProps {
  title: string
  children?: React.ReactNode
}

const ProspectInfoSection = ({
  title,
  children
}: ProspectInfoSectionProps) => (
  <div>
    <h3 className='text-xs text-gray-500 mb-2'>{title}</h3>
    <div className='text-sm text-gray-800'>
      {children}
    </div>
  </div>
)

export function ProspectInfo ({
  isForAdminView,
  prospect,
}: {
  isForAdminView: boolean
  prospect?: QuotingProspect | null
}) {
  return (
    <div className='flex flex-col gap-4'>
      <ProspectInfoSection title='Currently has benefits?'>
        {prospect
          ? (prospect.offers_health_insurance_today ? 'Yes' : 'No')
          : <SkeletonItem widthClass='w-1/3' />}
      </ProspectInfoSection>
      <ProspectInfoSection title='Expected coverage start date'>
        {prospect
          ? prospect.health_insurance_effective_date
            ? getFormattedDate(prospect.health_insurance_effective_date)
            : '-'
          : <SkeletonItem widthClass='w-1/2' />}
      </ProspectInfoSection>
      <ProspectInfoSection title='Date created'>
        {prospect
          ? prospect.created_at
            ? getFormattedDate(prospect.created_at)
            : '-'
          : <SkeletonItem widthClass='w-1/2' />}
      </ProspectInfoSection>
      {isForAdminView &&
        <>
          <ProspectInfoSection title='Brokerage'>
            {prospect
              ? prospect.partner_display_name
              : <SkeletonItem widthClass='w-2/3' />}
          </ProspectInfoSection>
          <ProspectInfoSection title='Requester'>
            {prospect
              ? prospect.owner_name_or_email
              : <SkeletonItem widthClass='w-1/2' />}
          </ProspectInfoSection>
          <ProspectInfoSection title="Requester's email">
            {prospect
              ? prospect.owner_email
              : <SkeletonItem />}
          </ProspectInfoSection>
        </>}
    </div>
  )
}

type Props = {
  isForAdminView: boolean;
  prospectId: string;
  showEIN?: boolean;
  showOwnerEmail?: boolean;
  quotingV2Enabled?: boolean
  quotingV2NewQuotePath?: string;
  quotingV2DynamicQuoteEditorPath?: string;
  quotingV2FlatQuoteEditorPath?: string;
  quotingV2QuoteList?: boolean;
}

function ProspectDetailsImpl ({
  isForAdminView = false,
  prospectId,
  quotingV2Enabled = false,
  quotingV2NewQuotePath,
  quotingV2QuoteList = false,
}: Props) {
  const { data: prospect, error, isLoading } = useGetQuotingProspectsId(prospectId)
  const [createQuoteError, setCreateQuoteError] = useState<GrapeAPIErrorType | null>(null)
  const [currentTab, setCurrentTab] = useState('partnerUploaded')

  const handleCreateQuoteError = (errorObj: GrapeAPIErrorType) => {
    setCreateQuoteError(errorObj)
  }

  const getUploadedFileCount = () => {
    if (currentTab === 'partnerUploaded') {
      return (prospect?.census_upload ? 1 : 0) + (prospect?.sbcs_uploads?.length ?? 0) + (prospect?.other_file_uploads?.length ?? 0)
    } else if (currentTab === 'thatchUploaded') {
      return 0
    }
    return 0
  }

  const uploadedFileCount = getUploadedFileCount()

  return (
    <>
      <div className='flex flex-col gap-y-4 pb-6'>
        <TextLink href={partnersProspects.index()} leftIcon={ChevronLeft}>Back to prospects</TextLink>
        <div className='flex justify-between items-center mb-4'>
          <h1 className='heading-lg'>
            {!isLoading && prospect ? prospect.name : <SkeletonItem />}
          </h1>
          {quotingV2Enabled && (quotingV2NewQuotePath
            ? (
              <div className='flex flex-col gap-3 items-end'>
                <ButtonLink
                  href={quotingV2NewQuotePath}
                  variant='primary'
                  leftIcon={UserPlusIcon}
                >
                  Create new quote
                </ButtonLink>
                <CreateQuoteButton
                  prospectId={prospectId}
                  quotingV2Enabled
                  onShowError={handleCreateQuoteError}
                />
              </div>)
            : (
              <CreateQuoteButton
                prospectId={prospectId}
                onShowError={handleCreateQuoteError}
              />
              ))}
        </div>
        {createQuoteError != null && <CreateQuoteFailed errorObj={createQuoteError} />}
        <div className='gap-8 flex flex-col lg:grid lg:grid-cols-[240px_1fr]'>
          <div>
            <h2 className='h2 font-normal pb-4'>Prospect Details</h2>
            {!isLoading && error != null && <GettingProspectFailed errorObj={error as GrapeAPIErrorType} />}
            <ProspectInfo isForAdminView={isForAdminView} prospect={prospect} />
          </div>
          <div className='flex flex-col gap-8'>
            <div>
              <h2 className='h2 font-normal pb-4'>Quotes</h2>
              <QuotesTable
                prospectId={prospectId}
                isForAdminView={isForAdminView}
                totalMonthlyContribution={prospect?.total_monthly_contribution}
              />
            </div>
            <div>
              <h2 className='h2 font-normal pb-4'>Files</h2>
              <Tabs defaultTab='partnerUploaded' onTabChange={setCurrentTab}>
                <Tabs.List>
                  <Tabs.Tab name='thatchUploaded'>Thatch uploaded files</Tabs.Tab>
                  <Tabs.Tab name='partnerUploaded'>{isForAdminView ? 'Partner' : 'Your'} uploaded files</Tabs.Tab>
                  {isForAdminView &&
                    <div hidden className='ml-auto my-auto'>
                      <Button variant='tertiary-deemphasized' leftIcon={FileArchive}>
                        Download {uploadedFileCount} file{uploadedFileCount !== 1 && 's'}
                      </Button>
                    </div>}
                </Tabs.List>
                <Tabs.Panel name='thatchUploaded'>
                  <div />
                </Tabs.Panel>
                <Tabs.Panel name='partnerUploaded'>
                  <div className='flex flex-col gap-4'>
                    <div>
                      <h3 className='h3 font-medium'>Census</h3>
                      {prospect?.census_upload
                        ? (
                          <FileList>
                            <FileList.File
                              key={prospect.census_upload.id}
                              name={prospect.census_upload.filename}
                              size={prospect.census_upload.byte_size}
                              status='success'
                              actions={
                                <IconLink
                                  label='Download'
                                  icon={Download}
                                  href={getGetQuotingProspectsProspectIdUploadsCensusUrl(prospectId)}
                                />
                              }
                            />
                          </FileList>)
                        : <p>None</p>}
                    </div>
                    <div>
                      <h3 className='h3 font-medium'>SBCs</h3>
                      {prospect?.sbcs_uploads?.length
                        ? (
                          <FileList>
                            {prospect.sbcs_uploads.map(file => (
                              <FileList.File
                                key={file.id}
                                name={file.filename}
                                size={file.byte_size}
                                status='success'
                                actions={
                                  <IconLink
                                    label='Download'
                                    icon={Download}
                                    href={getGetQuotingProspectsProspectIdUploadsSbcsBlobIdUrl(prospectId, file.id)}
                                  />
                                }
                              />))}
                          </FileList>)
                        : <p>None</p>}
                    </div>
                    <div>
                      <h3 className='h3 font-medium'>Other</h3>
                      {prospect?.other_file_uploads?.length
                        ? (
                          <FileList>
                            {prospect.other_file_uploads.map(file => (
                              <FileList.File
                                key={file.id}
                                name={file.filename}
                                size={file.byte_size}
                                status='success'
                                actions={
                                  <IconLink
                                    label='Download'
                                    icon={Download}
                                    href={getGetQuotingProspectsProspectIdUploadsOtherFilesBlobIdUrl(prospectId, file.id)}
                                  />
                                }
                              />))}
                          </FileList>)
                        : <p>None</p>}
                    </div>
                  </div>
                </Tabs.Panel>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
      {!quotingV2QuoteList && (
        <div>
          <h2 className='h2 font-normal pb-4'>Censuses</h2>
          <ProspectEmployeeCensusList
            isForAdminView={isForAdminView}
            prospectId={prospectId}
          />
        </div>
      )}
    </>
  )
}

export default function ProspectDetails ({
  isForAdminView = false,
  prospectId,
  showEIN = false,
  showOwnerEmail = false,
  quotingV2Enabled = false,
  quotingV2NewQuotePath,
  quotingV2DynamicQuoteEditorPath,
  quotingV2FlatQuoteEditorPath,
  quotingV2QuoteList = false,
}: Props) {
  return (
    <ProspectDetailsImpl
      isForAdminView={isForAdminView}
      prospectId={prospectId}
      showEIN={showEIN}
      showOwnerEmail={showOwnerEmail}
      quotingV2Enabled={quotingV2Enabled}
      quotingV2NewQuotePath={quotingV2NewQuotePath}
      quotingV2DynamicQuoteEditorPath={quotingV2DynamicQuoteEditorPath}
      quotingV2FlatQuoteEditorPath={quotingV2FlatQuoteEditorPath}
      quotingV2QuoteList={quotingV2QuoteList}
    />
  )
}
